import { render } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {Searcher} from 'fast-fuzzy';

import { Entry, useData } from './data';
import * as data from './data';
import { simpleSetSerializer, tinyCommandPolyfill, tinyDialogAutoclosePolyfill, useLocalStorage } from './utils';
import { ResultEntry } from './ResultList';
import { Navigation, SearchBox } from './SearchBox';
import { VirtualList } from './VirtualList';

declare const DEBUG: boolean;

const STDLIB_PACKAGES = new Set([
  "base",
  "contrib",
  "idris2",
  "linear",
  "network",
  "papers",
  "prelude",
  "test",
]);

if (DEBUG) {
  new EventSource('/esbuild').addEventListener('change', () => location.reload());
}

function App() {
  const [searchString, setSearchString] = useState('');
  const {searchIndex, load} = useData();
  const [matched, setMatched] = useState<Entry[]>([]);
  const [selected, setSelected] = useState<number>(0);
  const [enabledPackages, setEnabledPackages] = useLocalStorage<Set<string>>('quickdocs-enabled-packages', simpleSetSerializer, STDLIB_PACKAGES);

  const contentIframe = useRef<HTMLIFrameElement>(null);
  const searchBox = useRef<HTMLInputElement>(null);
  const allEntries = searchIndex?.entries;

  useEffect(() => {
    load();
  }, []);

  function handleGlobalShortcut(evt: KeyboardEvent) {
    if (evt.ctrlKey && evt.key === 'k') {
      console.log('ctrl+k');
      if (document.activeElement === searchBox.current) {
        return;
      }
      searchBox.current?.focus();
      searchBox.current?.select();
      evt.preventDefault();
      return false;
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalShortcut);
    return () => document.removeEventListener('keydown', handleGlobalShortcut);
  }, []);

  function selectItem(index: number) {
    if (index < 0 || !matched) {
      setSelected(0);
      return;
    }
    if (index >= matched.length) {
      setSelected(matched.length - 1);
      return;
    }
    setSelected(index);
  }

  const entriesPrefilteredByEnabledPackages = useMemo(() =>
    allEntries?.filter((e) => enabledPackages.has(e[data.IDX_PACKAGE]))
    , [enabledPackages, allEntries]) || [];

  const fuzzySearcher = useMemo(() =>
    new Searcher(entriesPrefilteredByEnabledPackages,
                 {ignoreSymbols: false,
                  keySelector: (e => [e[data.IDX_NAME],
                                      e[data.IDX_FULLNAME]])})
    , [entriesPrefilteredByEnabledPackages, allEntries]);

  useEffect(() => {
    if (!allEntries) {
      setMatched([]);
      return;
    }
    if (searchString === "") {
      setMatched(entriesPrefilteredByEnabledPackages);
      return;
    }
    const startTime = Number(new Date());
    setMatched(fuzzySearcher.search(searchString));
    const finishTime = Number(new Date());
    if (DEBUG) {
      console.log(`search for "${searchString}" in ${entriesPrefilteredByEnabledPackages.length} items took ${finishTime - startTime} ms`);
    }
    setSelected(0);
  }, [fuzzySearcher, searchString]);

  function search(query: string) {
    setSearchString(query.toLocaleLowerCase('en-US'));
  }

  function openResult(entry: Entry) {
    const href = `data/${entry[data.IDX_TARGET]}`;
    contentIframe.current!.src = href;
    const idx = matched.indexOf(entry);
    if (idx !== -1) {
      setSelected(idx);
    }
  }

  function handleIframeLoad(evt: Event) {
    const iframe = evt.target as HTMLIFrameElement;
    const iwindow = iframe.contentWindow!;

    if (iframe.src === "") {
      iframe.src = "data/home.html";
      return;
    }
    document.title = iwindow.document.title;

    iwindow.document.addEventListener('keydown', handleGlobalShortcut);
    addLinksToSource(iwindow.document);
  }

  function addLinksToSource(doc: Document) {
    for (const dt of doc.getElementsByTagName('dt')) {
      const srcModule = dt.getAttribute('data-src-mod');
      const srcPos = dt.getAttribute('data-src-start')?.split(':');
      if (srcModule === null || srcPos === undefined || srcPos.length !== 2) {
        continue;
      }
      const [line, _] = srcPos;
      // in Katla, the first line is "#line0":
      const katlaLine = parseInt(line) - 1;
      const link = `<a href="./${srcModule}.src.html#line${katlaLine}">Source</a>`;
      const linkEl = doc.createElement('span');
      linkEl.innerHTML = link;
      dt.append(linkEl);
      linkEl.style = 'float: right; opacity: 0.67;';
    }
  }

  function onNavigate(direction: Navigation) {
    switch(direction) {
      case Navigation.UP:
      selectItem(selected - 1);
      break;
      case Navigation.DOWN:
      selectItem(selected + 1);
      break;
      case Navigation.ENTER:
      openResult(matched[selected]);
      break;
    }
  }

  if (searchIndex === undefined) {
    return <div>Loading...</div>;
  }

  function setPackageEnabled(e: Event) {
    const checkbox = e.currentTarget as HTMLInputElement;
    const enabled = checkbox.checked;
    if (enabled) {
      setEnabledPackages(enabledPackages.union(new Set([checkbox.value])));
    } else {
      setEnabledPackages(enabledPackages.difference(new Set([checkbox.value])));
    }
  }

  function isBase(pkg: string): boolean {
    return STDLIB_PACKAGES.has(pkg);
  }

  const isThirdParty = (p: string) => (!isBase(p));

  return (
    <>
      <dialog id="package-selection" closedby="any" onClick={tinyDialogAutoclosePolyfill}>
        <section>
          <header>
            <h1>Packages</h1>
            <p>Choose packages enabled for search.</p>
            <div>
              <button onClick={() => setEnabledPackages(new Set())}>None</button>
              <button onClick={() => setEnabledPackages(STDLIB_PACKAGES)}>Only stdlib</button>
              <button onClick={() => setEnabledPackages(new Set(searchIndex.packages))}>All</button>
            </div>
          </header>
          <div class="dialog-body">
            <ul class="package-list">
              {
                searchIndex.packages.filter(isBase).map((pkg) => (
                  <li key={pkg}>
                    <input type="checkbox" id={`pkg-checkbox-${pkg}`} checked={enabledPackages.has(pkg)} value={pkg} onClick={setPackageEnabled} />
                    <label for={`pkg-checkbox-${pkg}`}>{pkg}</label>
                  </li>
                ))
              }
            </ul>
            <ul class="package-list">
              {
                searchIndex.packages.filter(isThirdParty).map((pkg) => (
                  <li key={pkg}>
                    <input type="checkbox" id={`pkg-checkbox-${pkg}`} checked={enabledPackages.has(pkg)} value={pkg} onClick={setPackageEnabled} />
                    <label for={`pkg-checkbox-${pkg}`}>{pkg}</label>
                  </li>
                ))
              }
            </ul>
          </div>
          <footer>
            <button commandfor="package-selection" command="request-close" onClick={tinyCommandPolyfill}>Done</button>
          </footer>
        </section>
      </dialog>

      <div id="sidebar">
        <div class="package-summary">
          <span class="package-count-info">
            Searching {enabledPackages.size} packages
          </span>
          <button commandfor="package-selection" command="show-modal" onClick={tinyCommandPolyfill}>Configure</button>
        </div>
        <SearchBox onNavigate={onNavigate} onSearch={search} inputRef={searchBox} />
        <VirtualList
          renderRow={(e: Entry, _, isSelected) =>
          <ResultEntry onClick={openResult} entry={e} key={e[data.IDX_KEY]} selected={isSelected} />
          }
          selected={selected}
          rowHeight={44}
          data={matched}/>
      </div>
      <div id="content">
        <iframe style="width: 100%; height: 100%;" ref={contentIframe}
          onLoad={handleIframeLoad}>
        </iframe>
      </div>
    </>
  );
}

function init() {
  render(<App />, document.getElementById('app')!);
}

document.addEventListener('DOMContentLoaded', init, { once: true });
if (document.readyState === 'interactive') {
  init();
}
